/* ============================================
   DUCK MANAGER - Gestion centralisée du ducking
   ============================================
   Gestionnaire unique qui :
   - Reçoit les demandes de duck de tous les one-shots actifs
   - Applique le duck le plus fort parmi tous les sons en cours
   - Ne restaure le volume qu'une fois TOUS les one-shots terminés
   - Évite le yo-yo de volume quand plusieurs sons jouent simultanément
   ============================================ */

export class DuckManager {
  /**
   * @param {AudioContext} ctx — AudioContext partagé
   * @param {GainNode} layer1Gain — GainNode de la couche 1 (ambiance)
   * @param {function} getBaseVolume — Retourne le volume de base Layer 1 (config.layer1Volume)
   */
  constructor(ctx, layer1Gain, getBaseVolume) {
    this.ctx = ctx
    this.layer1Gain = layer1Gain
    this.getBaseVolume = getBaseVolume

    /** @type {Map<string, number>} sourceId → duckLevel (0.0 = silence, 1.0 = pas de duck) */
    this.activeDucks = new Map()
  }

  /**
   * Enregistrer un duck pour un son en cours.
   * @param {string} sourceId — identifiant unique du son
   * @param {number} duckLevel — niveau cible (0.0 = silence, 1.0 = pas de duck)
   */
  requestDuck(sourceId, duckLevel) {
    this.activeDucks.set(sourceId, duckLevel)
    this._applyStrongestDuck()
  }

  /**
   * Libérer le duck quand un son se termine.
   * @param {string} sourceId — identifiant unique du son
   */
  releaseDuck(sourceId) {
    this.activeDucks.delete(sourceId)
    this._applyStrongestDuck()
  }

  /**
   * Appliquer le duck le plus fort (valeur la plus basse) parmi tous les ducks actifs.
   */
  _applyStrongestDuck() {
    const baseVolume = this.getBaseVolume()

    if (this.activeDucks.size === 0) {
      // Aucun son actif : restaurer Layer 1 (ramp douce 500ms)
      this._rampTo(baseVolume, 0.5)
      return
    }

    // Prendre le duck le plus fort (valeur la plus basse)
    const strongest = Math.min(...this.activeDucks.values())
    this._rampTo(baseVolume * strongest, 0.1)
  }

  /**
   * Rampe linéaire vers un volume cible.
   * @param {number} targetVolume — volume cible
   * @param {number} duration — durée de la rampe en secondes
   */
  _rampTo(targetVolume, duration) {
    if (!this.ctx || !this.layer1Gain) return

    const now = this.ctx.currentTime
    const gain = this.layer1Gain.gain

    gain.cancelScheduledValues(now)
    gain.setValueAtTime(gain.value, now)
    gain.linearRampToValueAtTime(targetVolume, now + duration)
  }

  /**
   * Libérer tous les ducks (reset).
   */
  releaseAll() {
    this.activeDucks.clear()
    const baseVolume = this.getBaseVolume()
    this._rampTo(baseVolume, 0.5)
  }
}
